import { AssetInvestigationType, InvestigationModel } from '@customer/store/apis/investigationsApi';
import { useAppDispatch } from '@customer/store/hooks';
import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InvestigationHeaderSummary from '../investigation-header-summary/InvestigationHeaderSummary';
import InvestigationStatusHeader from '../investigation-status-header/InvestigationStatusHeader';
import { ProtectedElement } from '@customer/components/state-management/protected-element/ProtectedElement';
import { Permission } from '@customer/permissions';
import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import RefreshButton from '@customer/components/inputs/refresh-button/RefreshButton';
import styles from './InvestigationHeader.module.scss';
import { QueryTag } from '@customer/store/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import { openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';

interface InvestigationHeaderProps {
  investigation: InvestigationModel | undefined;
}

export const InvestigationHeader = ({ investigation }: InvestigationHeaderProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const modelId = investigation?.id.toString();
  const investigationType = investigation?.it ?? AssetInvestigationType.Unknown;

  const tagsToClearOnRefresh = useMemo<TagDescription<QueryTag>[]>(
    () => [
      { type: QueryTag.Investigation, id: modelId },
      { type: QueryTag.InvestigationNotes, id: modelId },
      { type: QueryTag.InvestigationTracks, id: modelId },
      { type: QueryTag.InvestigationRawTracks, id: modelId },
    ],
    [modelId],
  );

  const onEdit = useCallback((): void => {
    if (modelId) {
      const sidePanel =
        investigationType === AssetInvestigationType.Custom
          ? SidePanel.EditCustomInvestigation
          : SidePanel.EditLoggedInvestigation;
      dispatch(openSidePanel({ sidePanel, modelId }));
    }
  }, [dispatch, modelId, investigationType]);

  return (
    <div className={styles.header}>
      <InvestigationHeaderSummary
        interactionType={investigation?.it ?? AssetInvestigationType.Unknown}
        investigation={investigation}
      />
      <div className={styles.actions}>
        {investigation && (
          <InvestigationStatusHeader investigationId={investigation.id} status={investigation.st} />
        )}
        <ProtectedElement permission={Permission.InvestigationsAll}>
          <VdsButton variant="secondary" onClick={onEdit} disabled={!investigation}>
            {t('Edit')}
            <VdsIcon slot="icon-left" svgIcon="edit" />
          </VdsButton>
        </ProtectedElement>
        <RefreshButton tagsToClear={tagsToClearOnRefresh} />
      </div>
    </div>
  );
};
